import { MouseEvent, useState } from 'react';
import { Box, Divider, Tooltip } from '@mui/material';
import { ControlButton } from 'reactflow';
import { ReactComponent as SettingsIcon } from '../../assets/settings.svg';
import useEnvPermission from '../../hooks/useEnvPermission';
import Checkbox from '../Checkbox';
import DisplayPopover, {
  DisplayPopoverOptionLabel,
  DisplayPopoverOptionRow,
  DisplayPopoverSectionLabel,
} from '../DisplayPopover';
import DropDown from '../DropDown';
import {
  LINEAGE_COLOR_BY_OPTIONS,
  LINEAGE_NODE_DETAIL_OPTIONS,
  LINEAGE_NODE_METADATA_OPTIONS,
} from './constants';
import { useLineageGraphContext } from './LineageGraphContext';
import type { LineageNodeMetadataField } from './types';

const NO_TESTS_PERMISSION_TOOLTIP = 'No permissions to view tests';

const LineageDisplaySettingsMenu = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { displaySettings, updateDisplaySettings, resetDisplaySettings } =
    useLineageGraphContext();
  const hasPermission = useEnvPermission();
  const hasTestReadScope = hasPermission('VIEW_TESTS');

  const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleNodeMetadataChange = (
    field: LineageNodeMetadataField,
    isVisible: boolean,
  ) => {
    updateDisplaySettings({
      nodeMetadata: { ...displaySettings.nodeMetadata, [field]: isVisible },
    });
  };

  const handleDetailFieldChange = (value: string) => {
    const option = LINEAGE_NODE_DETAIL_OPTIONS.find((o) => o.value === value);
    if (option) updateDisplaySettings({ detailField: option.value });
  };

  const handleColorByChange = (value: string) => {
    const option = LINEAGE_COLOR_BY_OPTIONS.find((o) => o.value === value);
    if (option) updateDisplaySettings({ colorBy: option.value });
  };

  const colorByOptions = LINEAGE_COLOR_BY_OPTIONS.map((option) =>
    option.requiresTests && !hasTestReadScope
      ? { ...option, disabled: true, description: NO_TESTS_PERMISSION_TOOLTIP }
      : option,
  );

  return (
    <>
      <Tooltip title="Display settings" placement="left">
        <ControlButton
          onClick={handleToggle}
          aria-label="display settings"
          aria-expanded={!!anchorEl}
        >
          <SettingsIcon />
        </ControlButton>
      </Tooltip>

      <DisplayPopover
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        title="Display settings"
        footerHint="Saved for the lineage page"
        onReset={resetDisplaySettings}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        isModal={false}
        clickAwayIgnoredEl={anchorEl?.closest('.react-flow__controls')}
      >
        <DisplayPopoverSectionLabel>Show on nodes</DisplayPopoverSectionLabel>
        <Box sx={{ px: 2, pt: 0.5, pb: 0.5 }}>
          <DropDown
            size="medium"
            fullWidth
            options={LINEAGE_NODE_DETAIL_OPTIONS}
            value={displaySettings.detailField}
            onChange={handleDetailFieldChange}
            showClear={false}
            sortOptions={false}
            selectedOnTop={false}
            aria-label="Detail line attribute"
          />
        </Box>
        {LINEAGE_NODE_METADATA_OPTIONS.map((option) => {
          const isDisabled = !!option.requiresTests && !hasTestReadScope;
          return (
            <Tooltip
              key={option.value}
              title={isDisabled ? NO_TESTS_PERMISSION_TOOLTIP : ''}
              placement="left"
            >
              <DisplayPopoverOptionRow>
                <Checkbox
                  $noPadding
                  sx={{ p: 0 }}
                  disabled={isDisabled}
                  checked={
                    !isDisabled && displaySettings.nodeMetadata[option.value]
                  }
                  onChange={(_, checked) =>
                    handleNodeMetadataChange(option.value, checked)
                  }
                  inputProps={{ 'aria-label': option.label }}
                />
                <DisplayPopoverOptionLabel>
                  {option.label}
                </DisplayPopoverOptionLabel>
              </DisplayPopoverOptionRow>
            </Tooltip>
          );
        })}

        <Divider sx={{ mt: 1.5 }} />

        <DisplayPopoverSectionLabel>Color by</DisplayPopoverSectionLabel>
        <Box sx={{ px: 2, pt: 0.5, pb: 1.5 }}>
          <DropDown
            size="medium"
            fullWidth
            options={colorByOptions}
            value={displaySettings.colorBy}
            onChange={handleColorByChange}
            showClear={false}
            sortOptions={false}
            selectedOnTop={false}
            aria-label="Color by"
          />
        </Box>
      </DisplayPopover>
    </>
  );
};

export default LineageDisplaySettingsMenu;
